/**
 * adminClient.ts — Website API admin endpoints
 * Dev: Vite proxies /api → http://127.0.0.1:8070 (WEBSITE_API_BASE empty)
 * Prod: same-origin /api (admin 子域 nginx 反代到 website_api)；可设 VITE_WEBSITE_API_URL 覆盖
 */

const envApiBase = (import.meta.env.VITE_WEBSITE_API_URL as string | undefined)?.trim();
const WEBSITE_API_BASE = envApiBase ?? '';
export { WEBSITE_API_BASE };
const ADMIN_TOKEN_STORAGE = 'mt_admin_token';

function parseApiErrorDetail(body: unknown, fallback: string): string {
  if (!body || typeof body !== 'object') return fallback;
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (item && typeof item === 'object' && 'msg' in item) {
          return String((item as { msg: unknown }).msg);
        }
        return String(item);
      })
      .filter(Boolean);
    if (parts.length) return parts.join('；');
  }
  return fallback;
}

async function readApiError(response: Response): Promise<string> {
  const fallback = response.statusText || `HTTP ${response.status}`;
  const text = await response.text();
  if (!text) return fallback;
  if (text.trimStart().startsWith('<!')) {
    return '无法连接管理 API（请确认当前域名已配置 /api 反代，或设置 VITE_WEBSITE_API_URL）';
  }
  try {
    return parseApiErrorDetail(JSON.parse(text), fallback);
  } catch {
    return text.slice(0, 200) || fallback;
  }
}

async function readApiJson<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type') || '';
  const text = await response.text();
  if (!text) {
    throw new Error('管理 API 返回空响应');
  }
  if (!contentType.includes('application/json') && text.trimStart().startsWith('<!')) {
    throw new Error('无法连接管理 API（当前域名未配置后端代理）');
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error('管理 API 返回格式异常');
  }
}

export interface AdminOverview {
  total_users: number;
  total_balance_cny: number;
  total_free_quota_cny: number;
  total_spent_cny: number;
  total_recharged_cny: number;
  active_users_30d: number;
}

export interface AdminUser {
  id: number;
  username: string;
  email: string | null;
  is_member: boolean;
  is_admin?: boolean;
  spent_cny: number;
  created_at: string;
  balance_cny: number;
  free_quota_cny: number;
  total_recharged: number;
  total_spent: number;
  total_available: number;
  allowed: boolean;
  last_billing_at: string | null;
}

export interface AdminUserList {
  items: AdminUser[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface AdminBillingRecord {
  id: number;
  user_id: number;
  request_type: string;
  provider: string;
  model: string | null;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cost_cny: number;
  status: string;
  created_at: string;
  error_message?: string | null;
}

export interface AdminUserDetail {
  user: AdminUser;
  summary: {
    total_requests: number;
    total_cost: number;
    total_tokens: number;
    by_provider: Record<string, { count: number; cost_cny: number }>;
    by_type: Record<string, { count: number; cost_cny: number }>;
  };
}

export interface AdminLoginResult {
  result: string;
  token: string;
  user_id: number;
  username: string;
}

export function getStoredAdminToken(): string {
  return sessionStorage.getItem(ADMIN_TOKEN_STORAGE) || '';
}

export function setStoredAdminToken(token: string): void {
  sessionStorage.setItem(ADMIN_TOKEN_STORAGE, token);
}

export function clearStoredAdminToken(): void {
  sessionStorage.removeItem(ADMIN_TOKEN_STORAGE);
}

function adminHeaders(): HeadersInit {
  const token = getStoredAdminToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function adminFetch<T>(path: string, init?: RequestInit): Promise<T> {
  let r: Response;
  try {
    r = await fetch(`${WEBSITE_API_BASE}${path}`, {
      ...init,
      headers: {
        ...adminHeaders(),
        ...(init?.headers || {}),
      },
    });
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    if (msg === 'Failed to fetch') {
      throw new Error('网络请求失败，请检查网络连接后重试');
    }
    throw e;
  }
  if (!r.ok) {
    throw new Error(await readApiError(r));
  }
  return readApiJson<T>(r);
}

export async function getAdminSetupStatus(): Promise<{ setup_available: boolean }> {
  let r: Response;
  try {
    r = await fetch(`${WEBSITE_API_BASE}/api/admin/setup-status`, {
      signal: AbortSignal.timeout(15000),
    });
  } catch {
    throw new Error('无法连接管理服务器');
  }
  if (!r.ok) {
    throw new Error(await readApiError(r));
  }
  return readApiJson<{ setup_available: boolean }>(r);
}

export async function fetchCaptchaConfig(): Promise<{ enabled: boolean; site_key: string }> {
  const r = await fetch(`${WEBSITE_API_BASE}/api/auth/captcha-config`);
  if (!r.ok) return { enabled: false, site_key: "" };
  return readApiJson<{ enabled: boolean; site_key: string }>(r);
}

export async function adminSetup(
  account: string,
  password: string,
  setupToken?: string,
  turnstileToken?: string,
): Promise<AdminLoginResult> {
  const body: Record<string, string> = { account, password };
  if (setupToken?.trim()) {
    body.setup_token = setupToken.trim();
  }
  if (turnstileToken) {
    body.turnstile_token = turnstileToken;
  }
  const r = await fetch(`${WEBSITE_API_BASE}/api/admin/setup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    throw new Error(await readApiError(r));
  }
  const data: AdminLoginResult = await readApiJson<AdminLoginResult>(r);
  setStoredAdminToken(data.token);
  return data;
}

export async function adminLogin(
  account: string,
  password: string,
  turnstileToken?: string,
): Promise<AdminLoginResult> {
  let r: Response;
  try {
    r = await fetch(`${WEBSITE_API_BASE}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        account,
        password,
        ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
      }),
    });
  } catch {
    throw new Error('无法连接管理服务器，请确认 website_api 已启动（端口 8070）');
  }
  if (!r.ok) {
    throw new Error(await readApiError(r));
  }
  const data: AdminLoginResult = await readApiJson<AdminLoginResult>(r);
  setStoredAdminToken(data.token);
  return data;
}

export async function getAdminOverview(): Promise<AdminOverview> {
  return adminFetch('/api/admin/overview');
}

export type AdminConfig = Record<string, string>;

export async function getAdminConfig(): Promise<AdminConfig> {
  return adminFetch('/api/admin/config');
}

export async function updateAdminConfig(
  data: Record<string, string>,
): Promise<{ result: string; message: string; updated: string[] }> {
  return adminFetch('/api/admin/config', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export interface ClientRemoteConfig {
  config_version: number;
  feature_byok: boolean;
  feature_batch_pipeline: boolean;
  feature_clip_batch_mode: boolean;
  ocr_batch_size: number;
  vectorize_text_chunk: number;
  vectorize_fusion_chunk: number;
  ocr_pipeline_max_retries: number;
  ocr_pipeline_retry_backoff_secs: number;
  pipeline_min_balance_cny: number;
  fusion_mode_default: boolean;
  force_fusion_mode: boolean;
  min_client_version?: string | null;
  notice_message?: string | null;
}

export async function getAdminClientConfig(): Promise<ClientRemoteConfig> {
  return adminFetch('/api/admin/client-config');
}

export async function updateAdminClientConfig(
  data: Partial<ClientRemoteConfig>,
): Promise<{ result: string; message: string; config: ClientRemoteConfig }> {
  return adminFetch('/api/admin/client-config', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export type AnnouncementStatus = 'draft' | 'published' | 'archived';

export interface Announcement {
  id: number;
  title: string;
  body: string;
  status: AnnouncementStatus;
  published_at: string | null;
  archived_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AnnouncementList {
  items: Announcement[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export async function listAdminAnnouncements(params?: {
  page?: number;
  limit?: number;
  status?: string;
}): Promise<AnnouncementList> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.status) searchParams.set('status', params.status);
  const qs = searchParams.toString();
  return adminFetch(`/api/admin/announcements${qs ? `?${qs}` : ''}`);
}

export async function getAdminCurrentAnnouncement(): Promise<{
  announcement: Announcement | null;
}> {
  return adminFetch('/api/admin/announcements/current');
}

export async function createAdminAnnouncement(data: {
  title: string;
  body: string;
  publish?: boolean;
}): Promise<{ result: string; message: string; announcement: Announcement }> {
  return adminFetch('/api/admin/announcements', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAdminAnnouncement(
  id: number,
  data: { title?: string; body?: string },
): Promise<{ result: string; message: string; announcement: Announcement }> {
  return adminFetch(`/api/admin/announcements/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function publishAdminAnnouncement(
  id: number,
): Promise<{ result: string; message: string; announcement: Announcement }> {
  return adminFetch(`/api/admin/announcements/${id}/publish`, { method: 'POST' });
}

export async function unpublishAdminAnnouncement(
  id: number,
): Promise<{ result: string; message: string; announcement: Announcement }> {
  return adminFetch(`/api/admin/announcements/${id}/unpublish`, { method: 'POST' });
}

export async function archiveAdminAnnouncement(
  id: number,
): Promise<{ result: string; message: string; announcement: Announcement }> {
  return adminFetch(`/api/admin/announcements/${id}/archive`, { method: 'POST' });
}

export async function deleteAdminAnnouncement(
  id: number,
): Promise<{ result: string; message: string }> {
  return adminFetch(`/api/admin/announcements/${id}`, { method: 'DELETE' });
}

export interface HealthStatus {
  status: string;
  config?: {
    dev_mode?: boolean;
    jwt_configured?: boolean;
    paddle_configured?: boolean;
    dashscope_configured?: boolean;
    google_configured?: boolean;
    email_configured?: boolean;
  };
}

export async function getHealthStatus(): Promise<HealthStatus> {
  return adminFetch('/api/admin/health');
}

export async function cleanupContentCache(): Promise<{ result: string; deleted?: number }> {
  return adminFetch('/api/admin/content-cache/cleanup', { method: 'POST' });
}

export async function fetchContentAssetBlob(contentHash: string): Promise<Blob> {
  const r = await fetch(`${WEBSITE_API_BASE}/api/admin/content-assets/${contentHash}/data`, {
    headers: adminHeaders(),
  });
  if (!r.ok) {
    throw new Error('Load failed');
  }
  return r.blob();
}

export async function listAdminUsers(params: {
  page?: number;
  limit?: number;
  q?: string;
}): Promise<AdminUserList> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.q) searchParams.set('q', params.q);
  const q = searchParams.toString();
  return adminFetch(`/api/admin/users${q ? `?${q}` : ''}`);
}

export async function getAdminUser(userId: number): Promise<AdminUserDetail> {
  return adminFetch(`/api/admin/users/${userId}`);
}

export async function getAdminUserRecords(
  userId: number,
  page = 1,
  limit = 20,
): Promise<{
  records: AdminBillingRecord[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  return adminFetch(`/api/admin/users/${userId}/records?${params}`);
}

export async function adminRechargeUser(
  userId: number,
  amountCny: number,
  description: string,
): Promise<{
  result: string;
  recharged_cny: number;
  description: string;
  balance_cny: number;
  total_recharged: number;
}> {
  return adminFetch(`/api/admin/users/${userId}/recharge`, {
    method: 'POST',
    body: JSON.stringify({ amount_cny: amountCny, description }),
  });
}

export async function adminSetMember(
  userId: number,
  isMember: boolean,
): Promise<{ result: string; user: AdminUser }> {
  return adminFetch(`/api/admin/users/${userId}/member`, {
    method: 'PUT',
    body: JSON.stringify({ is_member: isMember }),
  });
}

export async function adminCreateUser(params: {
  username: string;
  password: string;
  email?: string;
}): Promise<{ result: string; user: AdminUser }> {
  return adminFetch('/api/admin/users', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function adminSetPassword(
  userId: number,
  password: string,
): Promise<{ result: string; message: string }> {
  return adminFetch(`/api/admin/users/${userId}/password`, {
    method: 'PUT',
    body: JSON.stringify({ password }),
  });
}

export async function adminDeleteUser(
  userId: number,
): Promise<{ result: string; message: string }> {
  return adminFetch(`/api/admin/users/${userId}`, {
    method: 'DELETE',
  });
}

export interface AdminDailySummary {
  total_requests: number;
  active_users: number;
  total_tokens: number;
  total_cost_cny: number;
  success_count: number;
  failed_count: number;
  total_recharge_cny: number;
  recharge_count: number;
}

export interface AdminDailyModelRow {
  model: string;
  provider: string;
  request_count: number;
  total_tokens: number;
  cost_cny: number;
}

export interface AdminDailyTypeRow {
  request_type: string;
  request_count: number;
  total_tokens: number;
  cost_cny: number;
}

export interface AdminDailyUserRow {
  user_id: number;
  username: string;
  email: string | null;
  request_count: number;
  total_tokens: number;
  cost_cny: number;
}

export interface AdminDailyAnalysis {
  date: string;
  summary: AdminDailySummary;
  by_model: AdminDailyModelRow[];
  by_type: AdminDailyTypeRow[];
  by_user: AdminDailyUserRow[];
}

export async function getAdminDailyAnalysis(day?: string): Promise<AdminDailyAnalysis> {
  const params = new URLSearchParams();
  if (day) params.set('date', day);
  const q = params.toString();
  return adminFetch(`/api/admin/analysis/daily${q ? `?${q}` : ''}`);
}

export interface AdminContentCacheStats {
  total_assets: number;
  image_count: number;
  text_count: number;
  total_storage_bytes: number;
  total_cache_entries: number;
  total_cache_hits: number;
  embed_image_count: number;
  embed_text_count: number;
  embed_fused_count: number;
  ocr_count: number;
  logs_30d: number;
  cache_hits_30d: number;
  cache_hit_rate_30d: number;
  billed_cost_30d_cny: number;
  milvus?: AdminMilvusCacheStats;
}

export interface AdminMilvusCacheStats {
  enabled: boolean;
  backend: string;
  count: number;
  collection: string;
  dimension: number;
  db_path: string;
  db_exists?: boolean;
}

export interface AdminMilvusCacheEntry {
  image_hash: string;
  filename: string;
  created_at: string;
  dimension: number;
  source: string;
}

export interface AdminMilvusCacheList {
  items: AdminMilvusCacheEntry[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface AdminContentCacheEntry {
  cache_key: string;
  request_type: string;
  content_hash: string;
  auxiliary_hash: string | null;
  provider: string;
  model: string;
  dimension: number;
  hit_count: number;
  source_user_id: number | null;
  created_at: string;
  last_hit_at: string | null;
  content_type: string | null;
  byte_size: number | null;
  mime_type: string | null;
  text_length: number | null;
  text_preview?: string | null;
}

export interface AdminContentCacheList {
  items: AdminContentCacheEntry[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export async function getAdminContentCacheStats(): Promise<AdminContentCacheStats> {
  return adminFetch('/api/admin/content-cache/stats');
}

export async function getAdminContentCacheEntries(params: {
  page?: number;
  limit?: number;
  request_type?: string;
  content_type?: string;
}): Promise<AdminContentCacheList> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.request_type) searchParams.set('request_type', params.request_type);
  if (params.content_type) searchParams.set('content_type', params.content_type);
  const q = searchParams.toString();
  return adminFetch(`/api/admin/content-cache/entries${q ? `?${q}` : ''}`);
}

export async function getAdminMilvusCacheEntries(params: {
  page?: number;
  limit?: number;
}): Promise<AdminMilvusCacheList> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));
  const q = searchParams.toString();
  return adminFetch(`/api/admin/milvus-cache/entries${q ? `?${q}` : ''}`);
}

export interface AdminUserInferenceLog {
  id: number;
  user_id: number;
  cache_key: string;
  request_type: string;
  content_hash: string;
  was_cache_hit: number;
  cost_cny: number;
  created_at: string;
  username: string;
  content_type: string | null;
  mime_type: string | null;
  byte_size: number | null;
  text_preview: string | null;
  model: string | null;
  provider: string | null;
  auxiliary_preview?: string | null;
  ocr_result_preview?: string | null;
}

export interface AdminUserInferenceLogList {
  items: AdminUserInferenceLog[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export async function getAdminUserInferenceLogs(params: {
  page?: number;
  limit?: number;
  user_id?: number;
  request_type?: string;
}): Promise<AdminUserInferenceLogList> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.user_id) searchParams.set('user_id', String(params.user_id));
  if (params.request_type) searchParams.set('request_type', params.request_type);
  const q = searchParams.toString();
  return adminFetch(`/api/admin/user-inference-logs${q ? `?${q}` : ''}`);
}

export interface AdminDataResource {
  id: number;
  user_id: number;
  username: string;
  request_type: string;
  content_hash: string | null;
  auxiliary_hash: string | null;
  was_cache_hit: number;
  cost_cny: number;
  created_at: string;
  content_type: string | null;
  mime_type: string | null;
  byte_size: number | null;
  provider: string | null;
  model: string | null;
  has_text: boolean;
  has_image: boolean;
  text_preview: string | null;
  auxiliary_preview: string | null;
}

export interface AdminDataResourceList {
  items: AdminDataResource[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface AdminDataResourceDetail extends AdminDataResource {
  text_body: string | null;
  auxiliary_snapshot?: string | null;
}

export async function getAdminDataResources(params: {
  page?: number;
  limit?: number;
  user_id?: number;
  request_type?: string;
  content_type?: string;
  q?: string;
}): Promise<AdminDataResourceList> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.user_id) searchParams.set('user_id', String(params.user_id));
  if (params.request_type) searchParams.set('request_type', params.request_type);
  if (params.content_type) searchParams.set('content_type', params.content_type);
  if (params.q) searchParams.set('q', params.q);
  const q = searchParams.toString();
  return adminFetch(`/api/admin/data-resources${q ? `?${q}` : ''}`);
}

export async function getAdminDataResourceDetail(logId: number): Promise<AdminDataResourceDetail> {
  return adminFetch(`/api/admin/data-resources/${logId}`);
}

export async function downloadAdminDataResourcesExport(params: {
  user_id?: number;
  request_type?: string;
  content_type?: string;
  q?: string;
  format?: 'json' | 'csv';
}): Promise<void> {
  const searchParams = new URLSearchParams();
  if (params.user_id) searchParams.set('user_id', String(params.user_id));
  if (params.request_type) searchParams.set('request_type', params.request_type);
  if (params.content_type) searchParams.set('content_type', params.content_type);
  if (params.q) searchParams.set('q', params.q);
  const format = params.format || 'json';
  searchParams.set('format', format);
  const r = await fetch(`${WEBSITE_API_BASE}/api/admin/data-resources/export?${searchParams}`, {
    headers: adminHeaders(),
  });
  if (!r.ok) {
    throw new Error(await readApiError(r));
  }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = format === 'csv' ? 'data-resources.csv' : 'data-resources.json';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface AdminRechargeRecord {
  id: number;
  user_id: number;
  username: string;
  email: string | null;
  order_no: string;
  amount_cny: number;

  status: string;
  description: string;
  created_at: string;
  paid_at: string | null;
}

export interface AdminRechargeRecordList {
  items: AdminRechargeRecord[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface UserRechargeRecord {
  id: number;
  order_no: string;
  amount_cny: number;
  status: string;
  type: 'gift' | 'recharge';
  description: string;
  created_at: string;
  paid_at: string | null;
}

export interface UserRechargeRecordList {
  records: UserRechargeRecord[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export async function getAdminRechargeRecords(params: {
  page?: number;
  limit?: number;
  user_id?: number;
  status?: string;
}): Promise<AdminRechargeRecordList> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.user_id) searchParams.set('user_id', String(params.user_id));
  if (params.status) searchParams.set('status', params.status);
  const q = searchParams.toString();
  return adminFetch(`/api/admin/recharge-records${q ? `?${q}` : ''}`);
}

export async function getAdminUserRechargeRecords(
  userId: number,
  page = 1,
  limit = 20,
): Promise<UserRechargeRecordList> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  return adminFetch(`/api/admin/users/${userId}/recharge-records?${params}`);
}

